/**
 * useLlmApi hook
 * Manages LLM API data fetching and state
 * Only fetches once on mount - use refetch for manual updates
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { LlmProxyService } from '../../../services/llm-proxy.service.js';
import type { ModelWithUsage, UserLimits, UsageStats } from '../../../types/llm.types.js';

interface LlmApiState {
    models: ModelWithUsage[];
    limits: UserLimits | null;
    usage: UsageStats | null;
    loading: boolean;
    error: string | null;
}

export function useLlmApi(service: LlmProxyService) {
    const [state, setState] = useState<LlmApiState>({
        models: [],
        limits: null,
        usage: null,
        loading: true,
        error: null,
    });

    // Track if we've already fetched
    const hasFetched = useRef(false);

    const fetchData = useCallback(async (showLoading = false) => {
        // Show loading and clear error on fetch
        if (showLoading) {
            setState((prev) => ({ ...prev, loading: true, error: null }));
        }

        try {
            const [models, limits, usage] = await Promise.all([
                service.getModelsWithUsage(),
                service.getLimits(),
                service.getUsage(7),
            ]);

            setState({
                models,
                limits,
                usage,
                loading: false,
                error: null,
            });
        } catch (err) {
            setState((prev) => ({
                ...prev,
                loading: false,
                error: err instanceof Error ? err.message : 'Unknown error',
            }));
        }
    }, [service]);

    // Only fetch once on mount
    useEffect(() => {
        if (!hasFetched.current) {
            hasFetched.current = true;
            fetchData(true);
        }
    }, []);

    // Refetch with loading state (for retry after error)
    const refetch = useCallback(() => {
        fetchData(true);
    }, [fetchData]);

    return {
        ...state,
        refetch,
    };
}
